<template>
  <div
    :class="fullScreen ? 'pinwall__fileInput-fullscreen' : 'pinwall__fileInput'"
    :style="this.files.length > this.index ? 'margin-top: 24px' : ''"
  >
    <div
      v-if="(fullScreen || showButton) && this.files.length > this.index"
      class="info"
    >
      <span>
        {{ `Uploaded ${this.index} of ${this.files.length}` }}
      </span>
      <b-progress
        v-if="files.length > 0"
        type="is-info"
        size="is-medium"
        :value="progress"
        show-value
        >{{ progressText }}</b-progress
      >
    </div>
    <div v-if="fullScreen && !showButton">
      <b-upload
        v-if="showUploadInput"
        @input="onFileChange"
        multiple
        drag-drop
      ></b-upload>
    </div>
    <div v-if="fullScreen && showButton">
      <b-field class="file is-success upload-button">
        <b-upload
          @input="onFileChange"
          multiple
          class="file-label"
          :accept="accept"
        >
          <span class="file-cta">
            <b-icon class="file-icon" icon="plus"></b-icon>
          </span>
        </b-upload>
      </b-field>
    </div>
    <div v-if="!fullScreen && !showButton">
      <b-field v-if="!imageUrl && !videoUrl">
        <b-upload @input="onFileChange" multiple drag-drop :accept="accept">
          <section class="section">
            <div class="content has-text-centered">
              <p v-if="windowWidth > mobileWidth">
                <b-icon icon="upload"></b-icon>
              </p>
              <p v-if="!touch">{{ $t("filesUpload.dropFiles") }}</p>
              <p v-else>{{ $t("filesUpload.touchUpload") }}</p>
            </div>
          </section>
        </b-upload>
      </b-field>
      <div
        v-if="files.length > 1 && this.files.length > this.index"
        class="info-small"
      >
        <span>
          {{ `Uploaded ${this.index} of ${this.files.length}` }}
        </span>
      </div>
      <div
        class="preview"
        v-if="files.length < 2 && (imageUrl || videoUrl)"
        label="Preview"
      >
        <label class="label">Preview</label>
        <img v-if="!videoUrl" :src="imageUrl" />
        <video
          v-if="videoUrl"
          id="video"
          :src="videoUrl"
          controls
          playsinline
        />
      </div>
      <b-upload
        @input="onFileChange"
        multiple
        v-if="(imageUrl || videoUrl) && progress === 100 && files.length < 2"
      >
        <a class="button change-button is-rounded is-light">
          <p>{{ $t("change") }}</p>
        </a>
      </b-upload>
      <div>
        <b-progress
          v-if="files.length > 0"
          type="is-info"
          size="is-medium"
          :value="progress"
          show-value
          >{{ progressText }}</b-progress
        >
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from "vuex";
import { putObject, getObjectUrl } from "../../services/minio";
import {
  createThumbnail,
  resizeImage,
  ingestVideo
} from "../../services/media";

export default {
  name: "fileInput",
  computed: {
    ...mapGetters(["user", "tenant"])
  },
  props: {
    id: {
      type: String
    },
    fullScreen: {
      type: Boolean,
      default: false
    },
    showButton: {
      type: Boolean,
      default: false
    },
    accept: {
      type: String,
      default:
        ".htm,.html,.shtml,.xhtml,.doc,.docx,.pdf,.txt,.xls,.xlx,.zip,.ppt,.pptx,.mov,.m4v,.m4a,.ogg,.xml,.json,.vtt,.srt,image/jpeg,image/png,video/mp4,video/quicktime,video/x-m4v,video/ogg,audio/aac,audio/mp4,audio/mp3,audio/mpeg,audio/ogg,audio/wav,audio/flac,audio/m4a,audio/x-m4a,text/plain,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-powerpoint,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/pdf,application/zip,application/x-compressed,application/x-zip-compressed,multipart/x-zip,application/xml,text/xml,application/octet-stream,application/xhtml+xml,text/html,application/json,text/vtt"
    }
  },
  data: () => {
    return {
      imageUrl: null,
      videoUrl: null,
      image: null,
      video: null,
      showUploadInput: true,
      isLoading: true,
      touch: false,
      mobileWidth: process.env.VUE_APP_MOBILE_WIDTH,
      index: 0,
      files: [],
      progress: 0,
      progressText: "",
      videoConverted: false,
      thumbnailCreated: false,
      fileEmitted: false,
      proportion: 0
    };
  },
  created() {
    this.touch = "ontouchstart" in document.documentElement;
  },
  methods: {
    onFileChange(files) {
      this.$emit("fileUploading");
      this.files = files;
      this.uploadFile(this.files[0]);
      this.showUploadInput = false;
    },
    uploadNextFile() {
      this.index += 1;
      this.image = null;
      this.video = null;
      this.audio = null;
      this.fileEmitted = false;
      this.file = null;
      this.proportion = 0;
      this.progress = 0;
      this.progressText = "";
      this.uploadFile(this.files[this.index]);
    },
    uploadFile(file) {
      const _this = this;
      this.fileName = `${this.id}-${file.name}`;
      if (file.type.startsWith("image")) {
        this.uploadImage(`${this.fileName.split(".")[0]}.jpg`, file);
      } else if (file.type.startsWith("video")) {
        this.uploadVideo(this.fileName, file);
      } else {
        if (file.type.startsWith("audio/")) {
          this.proportion = 1.594405594405594;
          this.audio = {
            fileName: this.fileName,
            type: "local",
            mimeType: file.type
          };
        } else {
          this.file = { fileName: this.fileName, type: "local" };
        }
        this.progressText = _this.$t("filesUpload.fileUploading");
        putObject(this.tenant.tenantId, this.fileName, file, this.user).then(
          () => {
            this.progress = 100;
            this.progressText = _this.$t("filesUpload.finshed");
            this.index = this.files.length > 1 ? this.index : this.index + 1;
            this.$emit("fileUploaded", {
              proportion: this.proportion,
              image: this.image,
              video: this.video,
              audio: this.audio,
              file: this.file,
              pendingFiles: this.files.length - (this.index + 1)
            });
          }
        );
      }
    },
    uploadVideo(fileName, file) {
      const _this = this;
      this.video = { fileName: fileName, type: "local", mimeType: file.type };
      this.progressText = _this.$t("filesUpload.videoUploading");
      putObject(this.tenant.tenantId, fileName, file, this.user).then(() => {
        this.progress = 50;
        getObjectUrl(this.tenant.tenantId, fileName).then(url => {
          this.videoUrl = url;
          this.progressText = _this.$t("filesUpload.videoConverting");
          this.videoConverted = false;
          this.getThumbnail(this.tenant, fileName);
          ingestVideo({ fileName: fileName, tenant: this.tenant.tenantId })
            .then(() => {
              this.videoConverted = true;
              if (!this.fileEmitted && this.thumbnailCreated) {
                this.progress = 100;
                this.progressText = _this.$t("filesUpload.finshed");
                this.$emit("fileUploaded", {
                  proportion: this.proportion,
                  image: this.image,
                  video: this.video,
                  pendingFiles: this.files.length - (this.index + 1)
                });
              }
            })
            .catch(() => {});
        });
      });
    },
    uploadImage(fileName, file) {
      this.image = {
        type: "local",
        mimeType: file.type
      };
      const _this = this;
      const formData = new FormData();
      formData.append("image", file, "image.jpg");
      resizeImage({
        formData: formData,
        fileName: fileName,
        tenant: this.tenant.tenantId
      }).then(convertedFileName => {
        _this.progressText = !_this.video
          ? _this.$t("filesUpload.imageUploading")
          : _this.progressText;

        this.image.original = fileName;
        this.image.fileName = convertedFileName;

        putObject(_this.tenant.tenantId, fileName, file, this.user).then(() => {
          let imageSrc = `${process.env.VUE_APP_BASE_URL.toCorrectDomain()}/${
            process.env.VUE_APP_CDN_URL
          }${_this.tenant.tenantId}/${this.image.fileName}`;
          var img = new Image();
          img.onload = function() {
            _this.thumbnailCreated = true;
            _this.imageUrl = imageSrc;
            _this.proportion = this.width / this.height;

            if (!_this.video || _this.videoConverted) {
              _this.index =
                _this.files.length > 1 ? _this.index : _this.index + 1;
              _this.progress = 100;
              _this.progressText = _this.$t("filesUpload.finshed");
              _this.$emit("fileUploaded", {
                proportion: this.width / this.height,
                image: _this.image,
                video: _this.video,
                pendingFiles: _this.files.length - (_this.index + 1)
              });
            }
          };
          img.src = imageSrc;
        });
      });
    },
    getThumbnail(tenant, fileName) {
      const _this = this;
      this.thumbnailCreated = false;
      createThumbnail(tenant.tenantId, fileName).then(blob => {
        fileName = `${_this.fileName.split(".")[0]}.jpg`;
        this.image = {
          fileName: fileName,
          type: "local",
          mimeType: blob.type
        };
        putObject(_this.tenant.tenantId, fileName, blob, this.user).then(() => {
          let imageSrc = `${process.env.VUE_APP_BASE_URL.toCorrectDomain()}/${
            process.env.VUE_APP_CDN_URL
          }${_this.tenant.tenantId}/${this.image.fileName}`;
          var img = new Image();
          img.onload = function() {
            _this.thumbnailCreated = true;
            _this.imageUrl = imageSrc;
            _this.proportion = this.width / this.height;

            if (!_this.video || _this.videoConverted) {
              _this.index =
                _this.files.length > 1 ? _this.index : _this.index + 1;
              _this.progress = 100;
              _this.progressText = _this.$t("filesUpload.finshed");
              _this.$emit("fileUploaded", {
                proportion: this.width / this.height,
                image: _this.image,
                video: _this.video,
                pendingFiles: _this.files.length - (_this.index + 1)
              });
              _this.fileEmitted = true;
            }
          };
          img.src = imageSrc;
        });
      });
    }
  }
};
</script>
